import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, X, Search, Beaker, RefreshCw, Building2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacting", "Demo Scheduled", "Demo Completed", "Proposal Sent", "Negotiating", "Won", "Lost", "Not Qualified"];
const MODELS = ["subscription", "per_application", "per_policy", "hybrid"];
const CONTRACT = ["none", "sent", "signed"];
const PAYMENT = ["none", "invoiced", "paid"];
const IMPL_STEPS = ["not_started", "in_progress", "complete"];
const LAUNCH = ["not_ready", "in_progress", "ready", "launched"];

function dict(es) {
  return {
    title: es ? "Prospectos" : "Prospects",
    search: es ? "Buscar prospectos…" : "Search prospects…",
    allStatuses: es ? "Todos los estados" : "All statuses",
    empty: es ? "Aún no hay prospectos." : "No prospects yet.",
    agency: es ? "Agencia" : "Agency", contact: es ? "Contacto" : "Contact", status: es ? "Estado" : "Status",
    owner: es ? "Responsable" : "Owner", created: es ? "Creado" : "Created",
    sType: es ? "Tipo de agencia" : "Agency type", users: es ? "Usuarios" : "Users",
    lang: es ? "Idioma" : "Language", systems: es ? "Sistemas actuales" : "Current systems",
    challenge: es ? "Desafío principal" : "Primary challenge", volume: es ? "Volumen mensual" : "Monthly volume",
    contactMethod: es ? "Contacto preferido" : "Preferred contact", timing: es ? "Horario de demo" : "Demo timing",
    pipeline: es ? "Embudo de ventas" : "Sales pipeline", nextFu: es ? "Próximo seguimiento" : "Next follow-up",
    demoDate: es ? "Fecha de demo" : "Demo date", proposal: es ? "Monto de propuesta" : "Proposal amount",
    setupFee: es ? "Tarifa de configuración" : "Expected setup fee", monthlyFee: es ? "Tarifa mensual" : "Expected monthly fee",
    model: es ? "Modelo de facturación" : "Billing model", contract: es ? "Estado de contrato" : "Contract status",
    payment: es ? "Estado de pago" : "Payment status", launchDate: es ? "Fecha de lanzamiento" : "Expected launch date",
    implementation: es ? "Implementación" : "Implementation", implOwner: es ? "Responsable de implementación" : "Implementation owner",
    plan: es ? "Plan seleccionado" : "Selected plan", appRate: es ? "Tarifa por solicitud" : "Application rate",
    polRate: es ? "Tarifa por póliza" : "Policy rate", onboarding: es ? "Incorporación" : "Onboarding",
    dataImport: es ? "Importación de datos" : "Data import", providerCfg: es ? "Config. de proveedores" : "Provider config",
    training: es ? "Capacitación" : "Training", launchReady: es ? "Preparación de lanzamiento" : "Launch readiness",
    internalNotes: es ? "Notas internas" : "Internal notes", addNote: es ? "Agregar nota…" : "Add a note…",
    notesLog: es ? "Historial de notas" : "Notes log", save: es ? "Guardar" : "Save", saved: es ? "Guardado" : "Saved",
    convert: es ? "Convertir en agencia" : "Convert to agency", converted: es ? "Convertido en agencia" : "Converted to agency",
    convertHint: es ? "Solo un prospecto 'Won' puede convertirse." : "Only a 'Won' prospect can be converted.",
    convertConfirm: es ? "¿Crear una agencia real desde este prospecto?" : "Create a real agency from this prospect?",
    failed: es ? "Error" : "Failed", none: "—",
    demoTitle: es ? "Agencia de Demostración" : "Demo Agency",
    demoDesc: es ? "Agencia ficticia y aislada para demostraciones. Marcada como demo, excluida de facturación e ingresos, sin llamadas a proveedores." : "Fictional, isolated demo agency. Marked demo, excluded from billing & revenue, no provider calls.",
    seed: es ? "Crear datos de demo" : "Seed demo data", reset: es ? "Reiniciar demo" : "Reset demo",
    notSeeded: es ? "La agencia de demo aún no ha sido creada." : "The demo agency has not been seeded yet.",
    nonBillable: es ? "No facturable · solo demo" : "Non-billable · demo only",
  };
}

export function ProspectsPanel() {
  const { i18n } = useTranslation();
  const es = i18n.language === "es";
  const L = dict(es);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/platform/prospects", { params: { q, status } })
    .then(({ data }) => setRows(data)).catch(() => {}), [q, status]);
  useEffect(() => { const timer = setTimeout(load, q ? 300 : 0); return () => clearTimeout(timer); }, [load, q]);

  return (
    <div className="space-y-4" data-testid="platform-prospects">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L.search} className="w-64 pl-8" data-testid="prospect-search" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="prospect-status-filter"><SelectValue placeholder={L.allStatuses} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{L.allStatuses}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-lg border bg-card p-4">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2">{L.agency}</th><th>{L.contact}</th><th>{L.status}</th><th>{L.owner}</th><th>{L.created}</th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="cursor-pointer border-b hover:bg-muted/40" data-testid={`prospect-row-${p.id}`} onClick={() => setSel(p)}>
                <td className="py-2">{p.agency_name}<div className="text-xs text-muted-foreground">{p.display_id}</div></td>
                <td>{p.first_name} {p.last_name}<div className="text-xs text-muted-foreground">{p.business_email}</div></td>
                <td><span className="rounded-full bg-muted px-2 py-0.5 text-xs" data-testid={`prospect-status-${p.id}`}>{p.status}</span></td>
                <td className="text-xs">{p.owner || L.none}</td>
                <td className="text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : L.none}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">{L.empty}</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && <ProspectDrawer prospect={sel} L={L} onClose={() => setSel(null)} onSaved={(p) => { setSel(p); load(); }} busy={busy} setBusy={setBusy} />}
    </div>
  );
}

function ProspectDrawer({ prospect, L, onClose, onSaved, busy, setBusy }) {
  const [f, setF] = useState(prospect);
  const [note, setNote] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        status: f.status, owner: f.owner, next_followup_date: f.next_followup_date, demo_date: f.demo_date,
        proposal_amount: num(f.proposal_amount), expected_setup_fee: num(f.expected_setup_fee),
        expected_monthly_fee: num(f.expected_monthly_fee), billing_model: f.billing_model || undefined,
        contract_status: f.contract_status, payment_status: f.payment_status, expected_launch_date: f.expected_launch_date,
        implementation_owner: f.implementation_owner, selected_plan: f.selected_plan,
        application_rate: num(f.application_rate), policy_rate: num(f.policy_rate),
        onboarding_status: f.onboarding_status, data_import_status: f.data_import_status,
        provider_config_status: f.provider_config_status, training_status: f.training_status,
        launch_readiness_status: f.launch_readiness_status, internal_notes: f.internal_notes,
        add_note: note.trim() || undefined,
      };
      const { data } = await api.put(`/platform/prospects/${f.id}`, payload);
      setNote(""); onSaved(data); toast.success(L.saved);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || L.failed); }
    finally { setBusy(false); }
  };
  const convert = async () => {
    if (!window.confirm(L.convertConfirm)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/prospects/${f.id}/convert`, {});
      toast.success(`${L.converted}: ${data.agency_id}`);
      onSaved({ ...f, converted_agency_id: data.agency_id });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || L.failed); }
    finally { setBusy(false); }
  };

  const txt = (k, label, type = "text") => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Input type={type} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`prospect-${k}`} /></div>
  );
  const sel = (k, label, opts) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Select value={f[k] || opts[0]} onValueChange={(v) => set(k, v)}>
        <SelectTrigger data-testid={`prospect-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select></div>
  );
  const info = (label, v) => (<div><div className="text-[11px] text-muted-foreground">{label}</div><div>{v || L.none}</div></div>);

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/50" data-testid="prospect-drawer" onClick={onClose}>
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-card p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <div><div className="font-semibold">{f.agency_name}</div><div className="text-xs text-muted-foreground">{f.display_id} · {f.first_name} {f.last_name}</div></div>
          <button onClick={onClose} data-testid="prospect-drawer-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 rounded-lg border bg-muted/30 p-3 text-xs sm:grid-cols-3">
          {info(L.contact, f.business_email)}{info("", f.phone)}{info(L.sType, f.agency_type)}
          {info(L.users, f.num_users)}{info(L.lang, f.preferred_language)}{info(L.volume, f.monthly_lead_volume)}
          {info(L.systems, f.current_systems)}{info(L.challenge, f.primary_challenge)}
          {info(L.contactMethod, f.preferred_contact_method)}{info(L.timing, f.preferred_demo_timing)}
        </div>

        <div className="mt-4 text-sm font-semibold">{L.pipeline}</div>
        <div className="mt-2 grid grid-cols-2 gap-3">
          {sel("status", L.status, STATUSES)}{txt("owner", L.owner)}
          {txt("next_followup_date", L.nextFu, "date")}{txt("demo_date", L.demoDate, "date")}
          {txt("proposal_amount", L.proposal, "number")}{txt("expected_launch_date", L.launchDate, "date")}
          {txt("expected_setup_fee", L.setupFee, "number")}{txt("expected_monthly_fee", L.monthlyFee, "number")}
          {sel("billing_model", L.model, MODELS)}
          {sel("contract_status", L.contract, CONTRACT)}{sel("payment_status", L.payment, PAYMENT)}
        </div>

        <div className="mt-4 text-sm font-semibold">{L.implementation}</div>
        <div className="mt-2 grid grid-cols-2 gap-3">
          {txt("implementation_owner", L.implOwner)}{txt("selected_plan", L.plan)}
          {txt("application_rate", L.appRate, "number")}{txt("policy_rate", L.polRate, "number")}
          {sel("onboarding_status", L.onboarding, IMPL_STEPS)}{sel("data_import_status", L.dataImport, IMPL_STEPS)}
          {sel("provider_config_status", L.providerCfg, IMPL_STEPS)}{sel("training_status", L.training, IMPL_STEPS)}
          {sel("launch_readiness_status", L.launchReady, LAUNCH)}
        </div>
        <div className="mt-3 space-y-1"><Label className="text-xs">{L.internalNotes}</Label>
          <textarea value={f.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} data-testid="prospect-internal_notes"
            className="min-h-16 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" /></div>

        <div className="mt-3 space-y-1"><Label className="text-xs">{L.addNote}</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="prospect-add-note" /></div>
        {f.notes_log?.length > 0 && (
          <div className="mt-2 rounded-lg border bg-muted/30 p-2 text-xs" data-testid="prospect-notes-log">
            <div className="mb-1 font-semibold">{L.notesLog}</div>
            {f.notes_log.slice().reverse().map((n, i) => (
              <div key={i} className="border-t py-1 first:border-0"><span className="text-muted-foreground">{n.by} · {n.at ? new Date(n.at).toLocaleString() : ""}</span><div>{n.text}</div></div>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center gap-2">
          <Button onClick={save} disabled={busy} data-testid="prospect-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L.save}</Button>
          {f.converted_agency_id
            ? <span className="text-xs text-emerald-600" data-testid="prospect-converted">{L.converted}: {f.converted_agency_id}</span>
            : <Button variant="outline" onClick={convert} disabled={busy || f.status !== "Won"} title={f.status !== "Won" ? L.convertHint : ""} data-testid="prospect-convert">
                <Building2 className="mr-1 h-4 w-4" /> {L.convert}
              </Button>}
        </div>
        {f.status !== "Won" && !f.converted_agency_id && <div className="mt-1 text-[11px] text-muted-foreground">{L.convertHint}</div>}
      </div>
    </div>
  );
}

export function DemoPanel() {
  const { i18n } = useTranslation();
  const es = i18n.language === "es";
  const L = dict(es);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/platform/demo").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const seed = async (path) => {
    setBusy(true);
    try { const { data: r } = await api.post(`/platform/demo/${path}`); toast.success("OK"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || L.failed); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl space-y-4 rounded-lg border bg-card p-5" data-testid="platform-demo">
      <div className="flex items-center gap-2 font-semibold"><Beaker className="h-4 w-4 text-[#B28A3D]" /> {L.demoTitle}</div>
      <p className="text-sm text-muted-foreground">{L.demoDesc}</p>
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="demo-nonbillable">{L.nonBillable}</div>
      {data && !data.exists && <div className="text-sm text-muted-foreground" data-testid="demo-not-seeded">{L.notSeeded}</div>}
      {data?.exists && (
        <div className="grid grid-cols-3 gap-2 text-center text-xs sm:grid-cols-6" data-testid="demo-counts">
          {Object.entries(data.counts).map(([k, v]) => (
            <div key={k} className="rounded-lg border p-2"><div className="text-lg font-semibold tabular-nums">{v}</div><div className="text-muted-foreground">{k}</div></div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        {!data?.exists
          ? <Button onClick={() => seed("seed")} disabled={busy} data-testid="demo-seed"><Beaker className="mr-1 h-4 w-4" />{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L.seed}</Button>
          : <Button variant="outline" onClick={() => seed("reset")} disabled={busy} data-testid="demo-reset"><RefreshCw className="mr-1 h-4 w-4" />{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L.reset}</Button>}
      </div>
    </div>
  );
}
